import {supabase, supabaseUrl} from '@common/supabase/supabase-client';
import {renderSeoTags, setCustomSeoTemplates} from '@common/seo/render-seo-tags';
import {toBackendError} from '@common/supabase/backend-error';
import {setCurrentTenant, UnknownTenantError} from '@common/supabase/tenant';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import dot from 'dot-object';

/**
 * Values that Laravel merged into settings from config/.env. They describe what
 * this build supports, so they live in code (or VITE_ env vars) here.
 */
const configSettings = {
  // SaaS: helpdesks subscribe to the platform's plans (Razorpay).
  // WORKSPACES_ENABLED in Laravel's .env, off as shipped
  'billing.integrated': true,
  'workspaces.integrated': import.meta.env.VITE_WORKSPACES_ENABLED === 'true',
  'notifications.integrated': true,
  'notif.subs.integrated': true,
  'api.integrated': true,
  'websockets.integrated': true,
  'incoming_email.integrated': true,
  'site.demo': false,
  'site.hide_docs_buttons': false,
  'site.has_mobile_app': false,
  'uploading.disable_tus': true,
  'logging.sentry_public': import.meta.env.VITE_SENTRY_DSN ?? null,
};

// which upload types are publicly readable (Laravel UploadType::visibility)
const publicUploadTypes = ['avatars', 'brandingImages', 'articleImages', 'conversationImages'];

function getPreferredLocale(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)(?:locale|selected_locale)=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function cookieNoticeAccepted() {
  return /(?:^|;\s*)cookie_notice=/.test(document.cookie);
}

/**
 * The database of a fresh deploy has no tables yet, the setup wizard
 * (/install) explains what is missing.
 */
export class BackendNotInstalledError extends Error {}

export async function loadBootstrapData(): Promise<BootstrapData> {
  // make sure persisted session is restored before calling rpc, so it's
  // made with the user's access token
  const {
    data: {session},
  } = await supabase.auth.getSession();

  const [bootstrap, {data: seoTemplates}, {data: tenant}] = await Promise.all([
    supabase.rpc('get_bootstrap_data', {p_locale: getPreferredLocale()}),
    supabase.rpc('seo_tag_templates'),
    supabase.rpc('current_tenant_json'),
  ]);
  let {data} = bootstrap;
  const {error} = bootstrap;
  if (error) {
    // get_bootstrap_data doesn't exist: migrations were never applied
    if (error.code === 'PGRST202') throw new BackendNotInstalledError(error.message);
    throw toBackendError(error);
  }
  setCurrentTenant(tenant ?? null);
  if (!tenant) {
    throw new UnknownTenantError(`No helpdesk at ${window.location.host}`);
  }

  // logged in with an account from another helpdesk: join this one (as a
  // customer, or as an invited agent) if registration is open here
  if (session && !session.user.is_anonymous && !data.user) {
    const {error: joinError} = await supabase.rpc('join_current_tenant');
    if (!joinError) {
      const reloaded = await supabase.rpc('get_bootstrap_data', {p_locale: getPreferredLocale()});
      if (reloaded.error) throw toBackendError(reloaded.error);
      data = reloaded.data;
    }
  }
  setCustomSeoTemplates(seoTemplates);

  const flatSettings: Record<string, unknown> = {...data.settings};
  for (const [key, value] of Object.entries(configSettings)) {
    flatSettings[key] = value;
  }

  // settings are stored flat in dot notation, the UI expects nested objects.
  // Values for keys like "menus" are already json, so only object keys are
  // expanded.
  const settings = dot.object({...flatSettings}) as any;
  settings.base_url = window.location.origin;
  settings.asset_url = window.location.origin;
  settings.html_base_uri = '/';
  settings.version = import.meta.env.VITE_APP_VERSION ?? '3.0.8';
  settings.supabase_url = supabaseUrl;
  settings.websockets_setup = true;
  // live chat and AI agent backends: supabase/functions/livechat, ai,
  // livechat-reports and the 202610205* migrations
  settings.modules = {
    // "ai.setup" is maintained by a settings trigger: an LLM api key is configured
    ai: {installed: true, setup: !!settings.ai?.setup},
    envato: {installed: false, setup: false},
    livechat: {installed: true, setup: true},
  };
  // "aiAgent" setting mirrors the default AI agent (ai_agents table)
  settings.aiAgent = {
    enabled: !!settings.aiAgent?.enabled,
    name: settings.aiAgent?.name ?? 'AI assistant',
    image: settings.aiAgent?.image ?? null,
  };
  // only meaningful for agents (same as AppBootstrapData)
  if (data.user?.type !== 'agent') {
    settings.notif = {...settings.notif, subs: {integrated: false}};
  }

  const uploadTypes = settings.uploading?.types ?? {};
  const uploading_types = Object.fromEntries(
    Object.entries(uploadTypes).map(([name, config]: [string, any]) => [
      name,
      {
        visibility: publicUploadTypes.includes(name) ? 'public' : 'private',
        max_file_size: config.max_file_size,
        accept: config.accept,
        backends: [{id: 'supabase', driver: 'supabase'}],
      },
    ]),
  );

  return {
    ...data,
    settings,
    uploading_types,
    csrf_token: '',
    // AppBootstrapData::getDefaultMetaTags
    default_meta_tags: renderSeoTags('hc-landing-page', {}, settings),
    is_mobile_device: isMobileDevice(),
    show_cookie_notice: !!settings.cookie_notice?.enable && !cookieNoticeAccepted(),
    is_settings_preview: new URLSearchParams(window.location.search).get('settingsPreview') === 'true',
  } as BootstrapData;
}

/**
 * Reload bootstrap data after login, logout or profile changes. Equivalent of
 * Laravel returning fresh bootstrap data with auth responses.
 */
export async function reloadBootstrapData(): Promise<BootstrapData> {
  const data = await loadBootstrapData();
  setBootstrapData(data);
  return data;
}

/**
 * Custom css and html from the "Custom code" admin page. Laravel included
 * these files in the page layout, here they are injected once on boot.
 */
export function injectCustomCode(customCode?: {css?: string; html?: string}) {
  if (customCode?.css && !document.getElementById('be-custom-css')) {
    const style = document.createElement('style');
    style.id = 'be-custom-css';
    style.textContent = customCode.css;
    document.head.appendChild(style);
  }
  if (customCode?.html && !document.getElementById('be-custom-html')) {
    const container = document.createElement('div');
    container.id = 'be-custom-html';
    // createContextualFragment executes <script> tags, innerHTML would not
    container.appendChild(
      document.createRange().createContextualFragment(customCode.html),
    );
    document.body.appendChild(container);
  }
}
